import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type {
  OfficeAgentCode,
  OfficeAgentDto,
  OfficeCountsDto,
  OfficeItemDto,
  OfficeItemKind,
  OfficeLogDto,
  OfficeMode,
  OfficeMorningDto,
  OfficeStateDto,
  OfficeTecnicoDto,
} from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { ask } from '../dialogs';
import { Icon, type IconName } from '../icons';
import { ErrorBanner, Modal, useNow } from '../ui';
import { AGENT_COLOR, modeInfo, stateLabel } from './OfficeScene';
import { Tip } from './Tip';
import { roleText } from './OfficeScene';
import { OfficeMap, type MapBeat, type MapCall, type MapMember, type MapTransfer, type MapWaiting } from './OfficeMap';

/**
 * Ufficio: la squadra di assistenti al lavoro, in tempo reale.
 * Due viste della stessa cosa: la mappa (di base sul computer: tutta la squadra in una schermata) e le schede (di base sul telefono).
 * Un clic su un assistente apre la sua scheda: cosa fa, l'ultima decisione con il perché, il suo diario.
 * Sotto, il diario di tutta la squadra: ogni riga si può correggere (la correzione diventa una regola).
 */

const AGENT_ICON: Record<OfficeAgentCode, IconName> = { segreteria: 'mail', tecnico: 'server', opportunita: 'star' };
const AGENT_NAME: Record<OfficeAgentCode, string> = { segreteria: 'Segreteria', tecnico: 'Tecnico', opportunita: 'Opportunità' };

const KIND_ICON: Record<OfficeLogDto['kind'], IconName> = {
  letta: 'mail',
  decisione: 'checkCircle',
  azione: 'enter',
  prova: 'eye',
  avviso: 'bell',
  errore: 'x',
  riassunto: 'list',
  correzione: 'edit',
  giro: 'refresh',
  invio: 'send',
};

// «In prova» serve solo alla Segreteria (l'unica che sposta mail e scrive in agenda)
const MODES: Record<OfficeAgentCode, { mode: OfficeMode; label: string; text: string }[]> = {
  segreteria: [
    { mode: 'prova', label: 'Stagista', text: 'Scrive in «Cosa hanno fatto» cosa farebbe, ma non sposta mail e non tocca l’agenda.' },
    { mode: 'attivo', label: 'Operativa', text: 'Smista la posta e scrive in agenda; le risposte partono solo con il tuo sì.' },
    { mode: 'spento', label: 'Ferma', text: 'Non lavora finché non la riaccendi.' },
  ],
  tecnico: [
    { mode: 'attivo', label: 'Operativo', text: 'Controlla siti e server ogni 5 minuti e ti avvisa se qualcosa si ferma.' },
    { mode: 'spento', label: 'Fermo', text: 'Non controlla niente finché non lo riaccendi.' },
  ],
  opportunita: [
    { mode: 'attivo', label: 'Operativa', text: 'Cerca bandi e voucher alle 9 e alle 14, dal lunedì al venerdì.' },
    { mode: 'spento', label: 'Ferma', text: 'Non cerca niente finché non la riaccendi.' },
  ],
};

export function timeOf(iso: string, now: number): string {
  const d = new Date(iso);
  const t = d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  const today = new Date(now);
  if (d.toDateString() === today.toDateString()) return t;
  const y = new Date(now - 86_400_000);
  if (d.toDateString() === y.toDateString()) return `ieri ${t}`;
  return `${d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })} ${t}`;
}

function sameDay(iso: string, now: number): boolean {
  return new Date(iso).toDateString() === new Date(now).toDateString();
}

export function AgentAvatar({ code, size = 44 }: { code: OfficeAgentCode; size?: number }) {
  return (
    <span className="of-avatar" style={{ width: size, height: size, background: AGENT_COLOR[code] }}>
      <Icon name={AGENT_ICON[code]} size={Math.round(size * 0.48)} />
    </span>
  );
}

function StateChip({ a }: { a: OfficeAgentDto }) {
  return (
    <span className={`of-state ${a.mode === 'spento' ? 'off' : a.state}`}>
      <i />
      {stateLabel(a)}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Diario
// ---------------------------------------------------------------------------

export function DiaryRow({ e, ctx, now, onChanged, showAgent = true }: { e: OfficeLogDto; ctx: Ctx; now: number; onChanged: () => void; showAgent?: boolean }) {
  const [fixing, setFixing] = useState(false);
  const [details, setDetails] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const undo = async () => {
    if (!(await ask(`${e.undoLabel}? «${e.title}»`, { title: e.undoLabel ?? 'Annulla', ok: e.undoLabel ?? 'Sì' }))) return;
    setBusy(true);
    try {
      await ctx.api.post(`/api/office/log/${e.id}/undo`);
      onChanged();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (text.trim().length < 3) return;
    setBusy(true);
    try {
      await ctx.api.post(`/api/office/log/${e.id}/correct`, { text: text.trim() });
      setFixing(false);
      setText('');
      onChanged();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const canFix = ['decisione', 'azione', 'prova', 'avviso', 'invio'].includes(e.kind) && !e.correction;
  // Pagella: «Giusto» sulle decisioni che hanno una materia e non hanno ancora un voto (le risposte si votano approvandole)
  const canVote = !!e.topic && !e.verdict && !e.itemId && !e.undoneAt && !e.correction && ['decisione', 'azione', 'prova'].includes(e.kind);
  const right = async () => {
    setBusy(true);
    try {
      await ctx.api.post('/api/office/report/verdict', { logId: e.id, verdict: 'giusto' });
      onChanged();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={`of-row k-${e.kind}${e.undoneAt ? ' undone' : ''}${details ? ' expanded' : ''}`} style={{ '--ac': AGENT_COLOR[e.agent] } as React.CSSProperties}>
      <span className="of-row-ic">
        <Icon name={KIND_ICON[e.kind] ?? 'circle'} size={16} />
      </span>
      <div className="of-row-main">
        <div className="of-row-top">
          <b className="of-row-title" title={e.title}>{e.title}</b>
          {e.verdict && (
            <span className={`pill ${e.verdict === 'giusto' ? 'good' : e.verdict === 'ritoccato' ? 'warn' : 'bad'} of-verdict`} title="Il tuo voto per la Pagella">
              {e.verdict === 'giusto' ? 'Giusta' : e.verdict === 'ritoccato' ? 'Ritoccata' : 'Sbagliata'}
            </span>
          )}
          <span className="of-row-time">
            {showAgent && <span className="of-row-agent">{AGENT_NAME[e.agent]}</span>}
            {timeOf(e.at, now)}
          </span>
        </div>
        {(e.reason || (e.mailSubject !== null && e.mailFrom) || e.correction || e.undoneAt) && (
          <button type="button" className="of-row-detail-toggle" aria-expanded={details} onClick={() => setDetails((v) => !v)}>
            {details ? 'Chiudi dettagli' : 'Dettagli'} <Icon name={details ? 'chevronUp' : 'chevronDown'} size={13} />
          </button>
        )}
        {details && e.reason && (
          <div className="of-row-why">
            <span>{e.kind === 'errore' ? 'Cosa è successo:' : 'Perché:'}</span> {e.reason}
          </div>
        )}
        {details && e.mailSubject !== null && e.mailFrom && (
          <div className="of-row-mail">
            <Icon name="mail" size={13} /> «{e.mailSubject || 'senza oggetto'}» · {e.mailFrom}
          </div>
        )}
        {details && e.correction && (
          <div className="of-row-fix">
            <Icon name="edit" size={13} /> La tua correzione: {e.correction}
          </div>
        )}
        {details && e.undoneAt && <div className="of-row-fix muted">Annullato {timeOf(e.undoneAt, now)}</div>}
        <ErrorBanner message={error} onClose={() => setError(null)} />
        {fixing ? (
          <div className="of-fix-box">
            <textarea autoFocus rows={2} placeholder="Cosa doveva fare? Es. «Le mail di questo cliente lasciale sempre in arrivo»" value={text} onChange={(ev) => setText(ev.target.value)} />
            <div className="of-fix-actions">
              <span className="muted small">Diventa una regola: la squadra ne terrà conto da adesso.</span>
              <button type="button" className="btn small ghost" onClick={() => setFixing(false)}>
                Annulla
              </button>
              <button type="button" className="btn small dark" disabled={busy || text.trim().length < 3} onClick={save}>
                Salva la correzione
              </button>
            </div>
          </div>
        ) : (
          (canFix || canVote || (e.undoLabel && !e.undoneAt) || e.itemId) && (
            <div className="of-row-actions">
              {canVote && (
                <button type="button" className="btn small ghost" disabled={busy} title="La decisione è giusta: conta nella Pagella" onClick={right}>
                  <Icon name="check" size={14} /> Giusto
                </button>
              )}
              {e.undoLabel && !e.undoneAt && (
                <button type="button" className="btn small ghost" disabled={busy} onClick={undo}>
                  <Icon name="enter" size={14} /> {e.undoLabel}
                </button>
              )}
              {canFix && (
                <button type="button" className="btn small ghost" onClick={() => setFixing(true)}>
                  <Icon name="edit" size={14} /> Correggi
                </button>
              )}
              {e.itemId && (
                <button type="button" className="btn small ghost" onClick={() => ctx.setPage('approvals', { focus: e.itemId })}>
                  Apri <Icon name="chevronRight" size={14} />
                </button>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
}

function Diary({ ctx, entries, now, filter, setFilter, onMore, more, onChanged }: { ctx: Ctx; entries: OfficeLogDto[]; now: number; filter: OfficeAgentCode | 'tutti'; setFilter: (f: OfficeAgentCode | 'tutti') => void; onMore: () => void; more: boolean; onChanged: () => void }) {
  const list = filter === 'tutti' ? entries : entries.filter((e) => e.agent === filter);
  return (
    <section className="panel of-diary" id="of-diary">
      <header className="panel-head">
        <div className="grow">
          <h2>Cosa hanno fatto</h2>
          <div className="subtitle">Tutto quello che gli agenti leggono, decidono e fanno, con il perché</div>
        </div>
        <div className="of-filter" role="tablist">
          {(['tutti', 'segreteria', 'tecnico', 'opportunita'] as const).map((f) => (
            <button key={f} type="button" role="tab" aria-selected={filter === f} className={filter === f ? 'on' : ''} onClick={() => setFilter(f)}>
              {f === 'tutti' ? 'Tutti' : AGENT_NAME[f]}
            </button>
          ))}
        </div>
      </header>
      <div className="of-rows">
        {list.length === 0 && <div className="muted small of-empty">Ancora niente: le prime righe arrivano appena la squadra comincia a lavorare.</div>}
        {list.map((e) => (
          <DiaryRow key={e.id} e={e} ctx={ctx} now={now} onChanged={onChanged} />
        ))}
        {more && (
          <button type="button" className="btn small ghost of-more" onClick={onMore}>
            Mostra le righe più vecchie
          </button>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Schede
// ---------------------------------------------------------------------------

function AgentCard({ a, now, onOpen }: { a: OfficeAgentDto; now: number; onOpen: () => void }) {
  return (
    <button type="button" className={`of-card ${a.mode === 'spento' ? 'off' : a.state}`} style={{ '--ac': AGENT_COLOR[a.code] } as React.CSSProperties} onClick={onOpen}>
      <div className="of-card-head">
        <AgentAvatar code={a.code} />
        <div className="of-card-name">
          <b>{a.name}</b>
        </div>
        <Tip content={modeInfo(a).content} className={`pill ${a.mode === 'prova' ? 'warn' : 'soft'} of-trial-pill`}>{modeInfo(a).label}</Tip>
      </div>
      <div className="of-card-state">
        <StateChip a={a} />
        <span className="muted small">{timeOf(a.activityAt, now)}</span>
      </div>
      <div className="of-card-now" title={a.activity}>
        {a.mode === 'spento' ? 'Si riattiva dalla scheda' : a.state === 'attesa' && a.nextRunAt
          ? `Riprende alle ${new Date(a.nextRunAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`
          : a.activity.replace(/^Ultimo controllo: non funziona: /, 'Problema: ').replace(/^Ultimo controllo: tutto funziona$/, 'Tutto funziona')}
      </div>
      {a.progress && (
        <div className="of-progress">
          <i style={{ width: `${Math.round((a.progress.done / Math.max(1, a.progress.total)) * 100)}%` }} />
        </div>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Scheda di un assistente (si apre con un clic sul personaggio o sulla scheda)
// ---------------------------------------------------------------------------

function SegreteriaTools({ ctx, a }: { ctx: Ctx; a: OfficeAgentDto }) {
  const [info, setInfo] = useState<{ unread: number; running: boolean; done: number; total: number } | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const load = useCallback(() => {
    ctx.api
      .get<{ unread: number; running: boolean; done: number; total: number }>('/api/office/backlog')
      .then(setInfo)
      .catch(() => setInfo(null));
  }, [ctx.api]);
  useEffect(load, [load]);
  useEffect(() => {
    if (!a.progress && info?.running) load();
  }, [a.progress]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async (limit: number) => {
    const mins = Math.max(1, Math.round((limit * 12) / 60));
    if (!(await ask(`Smista ${limit === 1 ? '1 mail arretrata' : `${limit} mail arretrate`} in circa ${mins} minuti: le sposta soltanto, non risponde e non cancella.${a.mode === 'prova' ? ' È stagista: scrive solo in «Cosa hanno fatto».' : ''}`, { title: 'Mettere in ordine la posta arretrata?', ok: 'Sì, comincia' }))) return;
    await ctx.api.post('/api/office/backlog', { limit });
    setMsg('Cominciato: segui il lavoro qui sopra e in «Cosa hanno fatto».');
    load();
  };
  const running = !!a.progress || info?.running;
  return (
    <div className="of-tools">
      <h4>Posta arretrata</h4>
      {running ? (
        <div>
          <div className="of-progress">
            <i style={{ width: `${a.progress ? Math.round((a.progress.done / Math.max(1, a.progress.total)) * 100) : 0}%` }} />
          </div>
          <div className="of-tools-row">
            <span className="small">{a.progress ? `${a.progress.done} di ${a.progress.total}` : 'In corso…'}</span>
            <button type="button" className="btn small ghost" title="Ferma il riordino: le mail già sistemate restano dove sono" onClick={() => ctx.api.del('/api/office/backlog').then(load)}>
              Fermati
            </button>
          </div>
        </div>
      ) : info && info.unread > 0 ? (
        <>
          <p className="small">
            <b>{info.unread}</b> mail non lette arrivate prima della Segreteria: le metto in ordine?
          </p>
          <div className="of-tools-row">
            <button type="button" className="btn small dark" title="Smista le più recenti" onClick={() => start(Math.min(info.unread, 100))}>
              Le ultime {Math.min(info.unread, 100)}
            </button>
            {info.unread > 100 && (
              <button type="button" className="btn small ghost" onClick={() => start(info.unread)}>
                Tutte ({info.unread})
              </button>
            )}
          </div>
        </>
      ) : (
        <p className="muted small">{info ? 'Nessuna mail arretrata da smistare.' : 'Controllo…'}</p>
      )}
      {msg && <p className="small of-ok">{msg}</p>}
      <details className="of-reveal">
        <summary>Altri comandi</summary>
        <div className="of-tools-row wrap">
          <button type="button" className="btn small ghost" title="Cerca le mail che hai mandato e a cui nessuno ha risposto: prepara i solleciti" onClick={() => ctx.api.post('/api/office/followups').then(() => setMsg('Controllo le tue mail senza risposta: i solleciti arrivano in «Da approvare».'))}>
            <Icon name="reply" size={14} /> Le mie mail senza risposta
          </button>
          <button type="button" className="btn small ghost" title="Rilegge le tue ultime mail inviate per scrivere come te" onClick={() => ctx.api.post('/api/office/learn-style').then(() => setMsg('Studio di nuovo come scrivi: il risultato è in «Regole».'))}>
            <Icon name="edit" size={14} /> Studia di nuovo come scrivo
          </button>
        </div>
      </details>
    </div>
  );
}

function TecnicoTools({ ctx }: { ctx: Ctx }) {
  const [data, setData] = useState<OfficeTecnicoDto | null>(null);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(() => {
    ctx.api.get<OfficeTecnicoDto>('/api/office/tecnico').then(setData).catch((e) => setError((e as Error).message));
  }, [ctx.api]);
  useEffect(load, [load]);
  useEffect(() => {
    if (ctx.event?.type === 'office.agent' && ctx.event.agent.code === 'tecnico' && ctx.event.agent.state !== 'lavora') load();
  }, [ctx.eventSeq]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    setError(null);
    try {
      const u = /^https?:\/\//i.test(url.trim()) ? url.trim() : `https://${url.trim()}`;
      await ctx.api.post('/api/office/sites', { name: name.trim() || new URL(u).hostname, url: u });
      setName('');
      setUrl('');
      load();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const remove = async (id: number, n: string) => {
    if (!(await ask(`Eliminare «${n}» dai siti controllati?`))) return;
    await ctx.api.del(`/api/office/sites/${id}`);
    load();
  };
  const s = data?.server;
  const sites = data?.sites ?? [];
  const needsAttention = sites.filter((x) => x.status === 'giu' || x.status === 'lento' || (x.sslDays !== null && x.sslDays < 14));
  const healthy = sites.filter((x) => !needsAttention.includes(x));
  const siteRow = (x: OfficeTecnicoDto['sites'][number]) => (
    <div key={x.id} className={`of-site ${x.status ?? 'nuovo'}`}>
      <i />
      <div className="of-site-main" title={`${x.url}${x.note ? ` · ${x.note}` : ''}`}>
        <b>{x.name}</b>
        {x.status === 'giu' && x.note && <span className="tiny of-site-why">{x.sslDays !== null && x.sslDays < 0 ? 'Lucchetto scaduto' : x.note}</span>}
      </div>
      <span className="tiny muted of-site-meta">
        {x.status === 'giu' ? 'Problema' : x.status === 'lento' ? 'Lento' : x.sslDays !== null && x.sslDays < 14 ? 'Lucchetto in scadenza' : 'Funziona'}
      </span>
      <button type="button" className="btn icon small ghost" title={`Togli ${x.name} dai controlli`} onClick={() => remove(x.id, x.name)}>
        <Icon name="x" size={14} />
      </button>
    </div>
  );
  return (
    <div className="of-tools">
      <h4>Server CRM</h4>
      {s?.checkedAt ? (
        <div className="of-server">
          <span className={s.diskFreePct !== null && s.diskFreePct < 10 ? 'bad' : ''}>
            <b>{s.diskFreePct ?? '?'}%</b> disco libero
          </span>
          <span className={s.memFreePct !== null && s.memFreePct < 10 ? 'bad' : ''}>
            <b>{s.memFreePct ?? '?'}%</b> memoria libera
          </span>
          {s.load !== null && (
            <span>
              <b>{Math.round((s.load / Math.max(1, s.cpus)) * 100)}%</b> lavoro del processore
            </span>
          )}
        </div>
      ) : (
        <p className="muted small">Primo controllo tra poco…</p>
      )}
      <h4>Siti · {needsAttention.length ? `${needsAttention.length} da vedere` : 'tutto a posto'}</h4>
      <ErrorBanner message={error} onClose={() => setError(null)} />
      {needsAttention.length > 0 && <div className="of-sites">{needsAttention.map(siteRow)}</div>}
      {healthy.length > 0 && (
        <details className="of-reveal">
          <summary>{healthy.length} {healthy.length === 1 ? 'sito funziona' : 'siti funzionano'}</summary>
          <div className="of-sites">{healthy.map(siteRow)}</div>
        </details>
      )}
      <details className="of-reveal">
        <summary>Aggiungi un sito</summary>
        <div className="of-add-site">
          <input placeholder="Nome (es. Sito Rossi)" value={name} onChange={(e) => setName(e.target.value)} />
          <input placeholder="Indirizzo (es. rossi.it)" value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && url.trim() && add()} />
          <button type="button" className="btn small dark" disabled={!url.trim()} title="Lo controlla ogni 5 minuti da adesso" onClick={add}>Aggiungi</button>
        </div>
      </details>
    </div>
  );
}

function AgentDrawer({ ctx, a, now, onClose, onCommand }: { ctx: Ctx; a: OfficeAgentDto; now: number; onClose: () => void; onCommand: (code: OfficeAgentCode) => void }) {
  const [log, setLog] = useState<OfficeLogDto[]>([]);
  const [busy, setBusy] = useState(false);
  const [showAllLog, setShowAllLog] = useState(false);
  const load = useCallback(() => {
    ctx.api.get<OfficeLogDto[]>(`/api/office/log?agent=${a.code}&limit=40`).then(setLog).catch(() => undefined);
  }, [ctx.api, a.code]);
  useEffect(load, [load]);
  useEffect(() => {
    const ev = ctx.event;
    if (ev?.type === 'office.log' && ev.entry.agent === a.code) setLog((l) => (l.some((x) => x.id === ev.entry.id) ? l : [ev.entry, ...l].slice(0, 60)));
  }, [ctx.eventSeq]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onClose]);

  const setMode = async (mode: OfficeMode) => {
    if (mode === a.mode) return;
    if (mode === 'attivo' && a.code === 'segreteria' && !(await ask('Da adesso la Segreteria sposta davvero le mail e scrive in agenda. Le risposte partono sempre e solo con il tuo sì.', { title: 'Mettere la Segreteria al lavoro?', ok: 'Sì, attiva' }))) return;
    setBusy(true);
    try {
      await ctx.api.put(`/api/office/agents/${a.code}`, { mode });
    } finally {
      setBusy(false);
    }
  };
  const run = async () => {
    setBusy(true);
    try {
      await ctx.api.post(`/api/office/agents/${a.code}/run`);
      onCommand(a.code);
      onClose();
    } finally {
      setBusy(false);
    }
  };
  const modes = MODES[a.code];
  // Fuori dalla pagina (portale): copre anche il menu in basso sul telefono
  return createPortal(
    <div className="of-drawer-back" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside className="of-drawer" style={{ '--ac': AGENT_COLOR[a.code] } as React.CSSProperties} role="dialog" aria-label={a.name}>
        <header className="of-drawer-head">
          <AgentAvatar code={a.code} size={52} />
          <div className="grow">
            <h2>{a.name}</h2>
            <div className="muted small">{roleText(a)}</div>
          </div>
          <button type="button" className="btn icon ghost" title="Chiudi" onClick={onClose}>
            <Icon name="x" size={18} />
          </button>
        </header>
        <div className="of-drawer-body">
          <div className={`of-now ${a.mode === 'spento' ? 'off' : a.state}`}>
            <div className="of-now-top">
              <StateChip a={a} />
              <span className="muted small">{timeOf(a.activityAt, now)}</span>
            </div>
            <div className="of-now-text" title={a.activity}>{a.activity.replace(/^Ultimo controllo: non funziona: /, 'Problema: ').replace(/^Ultimo controllo: tutto funziona$/, 'Tutto funziona')}</div>
            {a.progress && (
              <div className="of-progress">
                <i style={{ width: `${Math.round((a.progress.done / Math.max(1, a.progress.total)) * 100)}%` }} />
              </div>
            )}
          </div>
          <div className="of-tools-row of-quick">
            <button type="button" className="btn small dark" disabled={busy || a.mode === 'spento' || a.state === 'lavora'} title="Lo fa subito, senza aspettare il prossimo giro" onClick={run}>
              <Icon name="refresh" size={14} /> {a.code === 'opportunita' ? 'Cerca adesso' : a.code === 'tecnico' ? 'Controlla adesso' : 'Guarda adesso la posta'}
            </button>
            <button type="button" className="btn small ghost" onClick={() => ctx.setPage('approvals', { tab: a.code === 'tecnico' ? 'avvisi' : a.code === 'opportunita' ? 'bandi' : 'risposte' })}>
              {a.code === 'tecnico' ? 'Vedi avvisi' : a.code === 'opportunita' ? 'Vedi bandi' : 'Vedi risposte'} <Icon name="chevronRight" size={14} />
            </button>
          </div>
          <div className="of-modes">
            <div className="of-label">Modalità</div>
            <div className="of-seg" role="radiogroup">
              {modes.map((m) => (
                <button key={m.mode} type="button" role="radio" aria-checked={a.mode === m.mode} className={a.mode === m.mode ? 'on' : ''} disabled={busy} title={m.text} onClick={() => setMode(m.mode)}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>
          {a.last && (
            <details className="of-last of-reveal">
              <summary>Ultima decisione · {timeOf(a.last.at, now)}</summary>
              <b>{a.last.title}</b>
              {a.last.reason && <p><span className="of-why">Perché:</span> {a.last.reason}</p>}
            </details>
          )}
          {a.code === 'segreteria' && <SegreteriaTools ctx={ctx} a={a} />}
          {a.code === 'tecnico' && <TecnicoTools ctx={ctx} />}
          <details className="of-history of-reveal">
            <summary>Attività recenti{log.length ? ` · ${log.length}` : ''}</summary>
            <div className="of-rows">
              {log.length === 0 && <div className="muted small">Ancora niente.</div>}
              {(showAllLog ? log : log.slice(0, 5)).map((e) => (
                <DiaryRow key={e.id} e={e} ctx={ctx} now={now} onChanged={load} showAgent={false} />
              ))}
              {log.length > 5 && !showAllLog && <button type="button" className="btn small ghost of-more" onClick={() => setShowAllLog(true)}>Mostra tutte</button>}
            </div>
          </details>
        </div>
      </aside>
    </div>,
    document.body,
  );
}

// ---------------------------------------------------------------------------
// Riassunto delle 8 e collegamento a Claude
// ---------------------------------------------------------------------------

function MorningCard({ m, onGo, onClose, compact = false, onExpand }: { m: OfficeMorningDto; onGo: (page: string, param?: unknown) => void; onClose: () => void; compact?: boolean; onExpand?: () => void }) {
  const at = new Date(m.at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  // Con la mappa il riassunto è una riga sola: la mappa resta tutta in una schermata
  if (compact) {
    const n = (k: number, one: string, many: string) => (k ? `${k} ${k === 1 ? one : many}` : '');
    const line =
      [
        n(m.decide.length, 'da decidere', 'da decidere'),
        n(m.callback.length, 'da richiamare', 'da richiamare'),
        n(m.today.length, 'impegno oggi', 'impegni oggi'),
        n(m.alerts.length, 'avviso', 'avvisi'),
        n(m.bandi.length, 'bando nuovo', 'bandi nuovi'),
      ]
        .filter(Boolean)
        .join(' · ') || 'Niente di nuovo';
    return (
      <section className="panel of-morning compact">
        <Icon name="list" size={16} />
        <b>Riassunto delle {at}</b>
        <span className="of-m-line">{line}</span>
        <button type="button" className="btn small ghost" title="Il riassunto completo: fatto, da decidere, da richiamare, agenda di oggi" onClick={onExpand}>
          Apri
        </button>
        <button type="button" className="btn icon ghost small" title="Nascondi fino al prossimo" onClick={onClose}>
          <Icon name="x" size={15} />
        </button>
      </section>
    );
  }
  const block = (title: string, list: string[], empty: string, go?: () => void) => (
    <div className="of-m-block">
      <div className="of-m-title">
        {title}
        {go && list.length > 0 && (
          <button type="button" className="of-m-go" onClick={go}>
            Apri <Icon name="chevronRight" size={13} />
          </button>
        )}
      </div>
      {list.length ? (
        <ul>
          {list.slice(0, 6).map((x, i) => (
            <li key={i}>{x}</li>
          ))}
          {list.length > 6 && <li className="muted">e altre {list.length - 6}</li>}
        </ul>
      ) : (
        <div className="muted small">{empty}</div>
      )}
    </div>
  );
  return (
    <section className="panel of-morning">
      <header className="panel-head">
        <div className="grow">
          <h2>Riassunto del mattino</h2>
          <div className="subtitle">Preparato alle {at}</div>
        </div>
        <button type="button" className="btn icon ghost" title="Nascondi fino al prossimo" onClick={onClose}>
          <Icon name="x" size={16} />
        </button>
      </header>
      <div className="of-m-grid">
        {block('Fatto dalla squadra', m.done, 'Niente da segnalare.')}
        {block('Da decidere', m.decide, 'Niente da approvare.', () => onGo('approvals'))}
        {block('Da richiamare', m.callback, 'Nessuno da richiamare.', () => onGo('approvals', { tab: 'richiamare' }))}
        {block('Oggi in agenda', m.today, 'Nessun impegno oggi.', () => onGo('agenda'))}
        {m.alerts.length > 0 && block('Avvisi', m.alerts, '', () => onGo('approvals', { tab: 'avvisi' }))}
        {m.bandi.length > 0 && block('Bandi nuovi', m.bandi, '', () => onGo('approvals', { tab: 'bandi' }))}
      </div>
    </section>
  );
}

function BrainModal({ ctx, state, onClose, onSaved }: { ctx: Ctx; state: OfficeStateDto; onClose: () => void; onSaved: (s: OfficeStateDto) => void }) {
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const save = async (value: string) => {
    setError(null);
    try {
      onSaved(await ctx.api.put<OfficeStateDto>('/api/office/brain', { token: value }));
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <Modal
      title="Collega Claude alla squadra"
      narrow
      onClose={onClose}
      footer={
        <>
          {state.brain.tokenSet && (
            <button type="button" className="btn ghost" title="La squadra si ferma finché non metti un codice nuovo" onClick={() => save('')}>
              Togli il codice
            </button>
          )}
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annulla
          </button>
          <button type="button" className="btn dark" disabled={!token.trim()} onClick={() => save(token)}>
            Collega
          </button>
        </>
      }
    >
      <div className="modal-body">
        <p>La squadra pensa con Claude, usando il tuo abbonamento: non si paga niente in più.</p>
        <p className="small muted" title="Sul computer con Claude Code scrivi «claude setup-token», entra con il tuo account Claude e copia il codice che inizia con «sk-ant-».">
          Si crea una volta sola con <b>claude setup-token</b> e vale un anno.
        </p>
        <ErrorBanner message={error} onClose={() => setError(null)} />
        <input className="of-token" type="password" autoComplete="off" placeholder="sk-ant-…" value={token} onChange={(e) => setToken(e.target.value)} />
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Pagina
// ---------------------------------------------------------------------------

// Le cose aperte di ogni assistente che aspettano te, per i numeri rossi sulla mappa
const KIND_WORD: Record<OfficeItemKind, [string, string]> = {
  risposta: ['risposta da approvare', 'risposte da approvare'],
  sollecito: ['sollecito da approvare', 'solleciti da approvare'],
  richiamare: ['persona da richiamare', 'persone da richiamare'],
  avviso: ['avviso', 'avvisi'],
  bando: ['bando da guardare', 'bandi da guardare'],
};

function waitingOf(items: OfficeItemDto[]): Partial<Record<OfficeAgentCode, MapWaiting>> {
  const by: Partial<Record<OfficeAgentCode, Partial<Record<OfficeItemKind, number>>>> = {};
  for (const it of items) {
    const m = (by[it.agent] ??= {});
    m[it.kind] = (m[it.kind] ?? 0) + 1;
  }
  const out: Partial<Record<OfficeAgentCode, MapWaiting>> = {};
  for (const [code, m] of Object.entries(by) as [OfficeAgentCode, Partial<Record<OfficeItemKind, number>>][]) {
    const parts = (Object.entries(m) as [OfficeItemKind, number][]).map(([k, n]) => `${n} ${KIND_WORD[k]?.[n === 1 ? 0 : 1] ?? k}`);
    out[code] = { n: Object.values(m).reduce((s, n) => s + (n ?? 0), 0), text: parts.join(', ') };
  }
  return out;
}

const openTotal = (c: OfficeCountsDto | undefined) => (c ? c.approve + c.callback + c.alerts + c.bandi : 0);

type View = 'map' | 'cards';

export function OfficePage({ ctx }: { ctx: Ctx }) {
  const me = ctx.me.user;
  const narrow = typeof window !== 'undefined' && window.matchMedia('(max-width: 760px)').matches;
  const viewKey = `tt.office.view.${me.id}`;
  // «office» era l'ufficio disegnato: adesso al suo posto c'è la mappa
  const [view, setViewState] = useState<View>(() => {
    const v = localStorage.getItem(viewKey);
    return v === 'cards' ? 'cards' : v === 'map' || v === 'office' ? 'map' : narrow ? 'cards' : 'map';
  });
  const setView = (v: View) => {
    localStorage.setItem(viewKey, v);
    setViewState(v);
  };
  const [items, setItems] = useState<OfficeItemDto[]>([]);
  const [beats, setBeats] = useState<Record<MapBeat, number>>({ segreteria: 0, tecnico: 0, opportunita: 0, me: 0 });
  const bump = useCallback((k: MapBeat) => setBeats((b) => ({ ...b, [k]: b[k] + 1 })), []);
  const [transfers, setTransfers] = useState<MapTransfer[]>([]);
  // Chiamata di prova all'agente telefonico: le frasi dette, dal vivo (non vengono salvate)
  const [call, setCall] = useState<MapCall | null>(null);
  const callId = useRef('');
  const callTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const transferId = useRef(0);
  const transferTimers = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());
  const sendTransfer = useCallback((from: MapMember, to: MapMember) => {
    const id = ++transferId.current;
    setTransfers((current) => [...current.slice(-4), { id, from, to }]);
    const timer = setTimeout(() => {
      setTransfers((current) => current.filter((transfer) => transfer.id !== id));
      transferTimers.current.delete(timer);
    }, 2500);
    transferTimers.current.add(timer);
  }, []);
  const knownItemIds = useRef<Set<number> | null>(null);
  const itemsTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [state, setState] = useState<OfficeStateDto | null>(null);
  const [log, setLog] = useState<OfficeLogDto[]>([]);
  const [more, setMore] = useState(true);
  const [filter, setFilter] = useState<OfficeAgentCode | 'tutti'>('tutti');
  const [open, setOpen] = useState<OfficeAgentCode | null>(null);
  const [brainOpen, setBrainOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hiddenMorning, setHiddenMorning] = useState(() => localStorage.getItem(`tt.office.morningHidden.${me.id}`) ?? '');
  // Con la mappa il riassunto è una riga: si apre con «Apri» o con il pulsante «Riassunto»
  const [morningOpen, setMorningOpen] = useState(false);
  const now = useNow(20_000);

  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([ctx.api.get<OfficeStateDto>('/api/office'), ctx.api.get<OfficeLogDto[]>('/api/office/log?limit=60')]);
      setState(s);
      setLog(l);
      setMore(l.length >= 60);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [ctx.api]);
  useEffect(() => {
    void load();
  }, [load]);

  // Mappa: le cose aperte di ogni assistente (i numeri rossi)
  const loadItems = useCallback((detectNew = false) => {
    ctx.api.get<OfficeItemDto[]>('/api/office/items?status=aperto').then((fresh) => {
      const previous = knownItemIds.current;
      if (detectNew && previous) {
        const senders = new Set(fresh.filter((item) => !previous.has(item.id) && Date.now() - Date.parse(item.createdAt) < 60_000).map((item) => item.agent));
        senders.forEach((agent) => sendTransfer(agent, 'me'));
      }
      knownItemIds.current = new Set(fresh.map((item) => item.id));
      setItems(fresh);
    }).catch(() => undefined);
  }, [ctx.api, sendTransfer]);
  useEffect(() => {
    if (view === 'map') loadItems();
  }, [view, loadItems]);
  useEffect(
    () => () => {
      if (itemsTimer.current) clearTimeout(itemsTimer.current);
      transferTimers.current.forEach(clearTimeout);
    },
    [],
  );

  // Tempo reale: stato degli assistenti, righe nuove del diario, cose da approvare, riassunto
  useEffect(() => {
    const ev = ctx.event;
    if (!ev) return;
    if (ev.type === 'office.agent') setState((s) => (s ? { ...s, agents: s.agents.map((a) => (a.code === ev.agent.code ? ev.agent : a)) } : s));
    else if (ev.type === 'office.log') {
      setLog((l) => (l.some((x) => x.id === ev.entry.id) ? l : [ev.entry, ...l]));
      // Sulla mappa pulsa il pallino dell'assistente
      if (ev.entry.kind !== 'riassunto') bump(ev.entry.agent);
      if (ev.entry.agent === 'segreteria' && /passata al Tecnico/i.test(ev.entry.title)) sendTransfer('segreteria', 'tecnico');
      if (ev.entry.agent === 'segreteria' && /passata a Opportunità/i.test(ev.entry.title)) sendTransfer('segreteria', 'opportunita');
    } else if (ev.type === 'office.items') {
      // Qualcosa di nuovo per te: pulsa anche il tuo riquadro
      const increased = openTotal(ev.counts) > openTotal(state?.counts);
      if (increased) setTimeout(() => bump('me'), 1100);
      setState((s) => (s ? { ...s, counts: ev.counts } : s));
      if (view === 'map') {
        if (itemsTimer.current) clearTimeout(itemsTimer.current);
        itemsTimer.current = setTimeout(() => loadItems(increased), 600);
      }
    } else if (ev.type === 'office.morning') setState((s) => (s ? { ...s, morning: ev.morning } : s));
    else if (ev.type === 'office.call') {
      const c = ev.call;
      if (callTimer.current) clearTimeout(callTimer.current);
      if (c.event === 'start') {
        callId.current = c.id;
        setCall({ active: true, lines: [] });
      } else if (c.event === 'line' && c.text && c.who) {
        const line = { who: c.who, text: c.text };
        setCall((x) => (x && callId.current === c.id ? { ...x, lines: [...x.lines, line] } : { active: true, lines: [line] }));
        callId.current = c.id;
      } else if (c.event === 'end' && callId.current === c.id) {
        setCall((x) => (x ? { ...x, active: false } : x));
        // la conversazione resta sullo schermo ancora un po', poi sparisce
        callTimer.current = setTimeout(() => setCall(null), 30_000);
      }
    }
  }, [ctx.eventSeq]); // eslint-disable-line react-hooks/exhaustive-deps
  // Ogni tanto rileggo lo stato del cervello (chiamate di oggi) e rimetto a posto se ho perso qualche evento
  useEffect(() => {
    const t = setInterval(() => {
      ctx.api
        .get<OfficeStateDto>('/api/office')
        .then(setState)
        .catch(() => undefined);
      if (view === 'map') loadItems();
    }, 60_000);
    return () => clearInterval(t);
  }, [ctx.api, view, loadItems]);

  const loadMore = async () => {
    const last = log[log.length - 1];
    if (!last) return;
    const older = await ctx.api.get<OfficeLogDto[]>(`/api/office/log?limit=60&before=${last.id}`);
    setLog((l) => [...l, ...older]);
    setMore(older.length >= 60);
  };

  const morningNow = async () => {
    try {
      const m = await ctx.api.post<OfficeMorningDto>('/api/office/morning');
      setState((s) => (s ? { ...s, morning: m } : s));
      setHiddenMorning('');
      setMorningOpen(true);
      localStorage.removeItem(`tt.office.morningHidden.${me.id}`);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const agents = state?.agents ?? [];
  const working = agents.filter((a) => a.mode !== 'spento' && a.state === 'lavora').length;
  const waiting = agents.filter((a) => a.mode !== 'spento' && a.state === 'attesa').length;
  const problems = agents.filter((a) => a.mode !== 'spento' && a.state === 'errore').length;
  const counts = state?.counts;
  const toApprove = counts ? counts.approve : 0;
  const opened = agents.find((a) => a.code === open) ?? null;
  const showMorning = state?.morning && sameDay(state.morning.at, now) && hiddenMorning !== state.morning.at;
  const mapOn = view === 'map' && !!state;

  return (
    <div className="of-page">
      {/* In alto quello che sta in una schermata: con la mappa riempie la pagina, il diario è appena sotto */}
      <div className={`of-top${mapOn ? ' full' : ''}`}>
        <div className="of-head">
          <div>
            <h1>La tua squadra</h1>
            <div className={`of-brain ${state?.brain.ready === false ? 'bad' : ''}`} title={state?.brain.ready ? `${state.brain.callsToday} chiamate oggi` : undefined}>
              <span className="of-brain-dot" />
              {state ? (
                <>
                  {state.brain.ready ? 'Sistema pronto' : 'Squadra in pausa'}
                </>
              ) : (
                'Caricamento…'
              )}
              {state && !mapOn && state.brain.canSetToken && (!state.brain.ready || state.brain.tokenSet) && (
                <button type="button" className="of-link" title="Impostazioni del collegamento" onClick={() => setBrainOpen(true)}>
                  {state.brain.ready ? 'Impostazioni' : 'Collega'}
                </button>
              )}
            </div>
          </div>
          <div className="of-head-right">
            {/* Con la mappa i numeri sono già sui pallini */}
            {!mapOn && (
              <div className="of-counters">
                <span className="of-counter good">
                  <b>{working}</b> al lavoro
                </span>
                <span className="of-counter">
                  <b>{waiting}</b> in attesa
                </span>
                {problems > 0 && (
                  <span className="of-counter bad">
                    <b>{problems}</b> con un problema
                  </span>
                )}
                <button type="button" className={`of-counter warn${toApprove ? ' strong' : ''}`} title="Le cose preparate che aspettano il tuo sì" onClick={() => ctx.setPage('approvals')}>
                  <b>{toApprove}</b> da approvare
                </button>
                {(counts?.toCheck ?? 0) > 0 && (
                  <button type="button" className="of-counter" title="Decisioni da controllare con un clic: servono alla Pagella" onClick={() => ctx.setPage('office-report')}>
                    <b>{counts!.toCheck}</b> da controllare
                  </button>
                )}
              </div>
            )}
            <div className="of-switch" role="tablist" aria-label="Vista">
              <button type="button" role="tab" aria-selected={view === 'map'} className={view === 'map' ? 'on' : ''} title="Tutta la squadra in una schermata: chi lavora, cosa fa, cosa aspetta te" onClick={() => setView('map')}>
                Mappa
              </button>
              <button type="button" role="tab" aria-selected={view === 'cards'} className={view === 'cards' ? 'on' : ''} title="Una scheda per assistente, comoda sul telefono" onClick={() => setView('cards')}>
                Schede
              </button>
            </div>
            <button type="button" className="btn small ghost" title="Il riassunto di adesso: fatto, da decidere, da richiamare, agenda di oggi" onClick={morningNow}>
              <Icon name="list" size={15} /> Riassunto
            </button>
          </div>
        </div>
        <ErrorBanner message={error} onClose={() => setError(null)} />
        {state && !state.brain.ready && (
          <div className="of-alertbar">
            <Icon name="bell" size={18} />
            <span>
              <b>La squadra è in pausa:</b> controlla il collegamento.
            </span>
            {!mapOn && state.brain.canSetToken && (
              <button type="button" className="btn small dark" onClick={() => setBrainOpen(true)}>
                Collega la squadra
              </button>
            )}
          </div>
        )}
        {showMorning && state?.morning && (
          <MorningCard
            m={state.morning}
            onGo={ctx.setPage}
            compact={mapOn && !morningOpen}
            onExpand={() => setMorningOpen(true)}
            onClose={() => {
              setMorningOpen(false);
              localStorage.setItem(`tt.office.morningHidden.${me.id}`, state.morning!.at);
              setHiddenMorning(state.morning!.at);
            }}
          />
        )}
        {!state ? (
          <div className="panel muted">Caricamento della squadra…</div>
        ) : view === 'map' ? (
          <OfficeMap
            agents={agents}
            counts={state.counts}
            meName={me.name.split(' ')[0] || 'Tu'}
            waiting={waitingOf(items)}
            beats={beats}
            transfers={transfers}
            call={call}
            live={ctx.live}
            onPick={setOpen}
            onMe={() => ctx.setPage('approvals')}
            onHub={() => document.getElementById('of-diary')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          />
        ) : (
          <div className="of-cards">
            {agents.map((a) => (
              <AgentCard key={a.code} a={a} now={now} onOpen={() => setOpen(a.code)} />
            ))}
          </div>
        )}
      </div>
      <Diary ctx={ctx} entries={log} now={now} filter={filter} setFilter={setFilter} onMore={loadMore} more={more} onChanged={load} />
      {opened && <AgentDrawer ctx={ctx} a={opened} now={now} onClose={() => setOpen(null)} onCommand={(code) => sendTransfer('me', code)} />}
      {brainOpen && state && <BrainModal ctx={ctx} state={state} onClose={() => setBrainOpen(false)} onSaved={setState} />}
    </div>
  );
}
